/**
 * CreatorSubscriptionBoundsPanel — Admin: Preisgrenzen für Creator-Abos.
 * iter122 · Spec: /app/memory/SPEC_SUBSCRIPTION_AND_DIGITAL_CONTENT.md §1
 *
 * Wird als Sub-Sektion unter *Servicegebühren* im Admin-Dashboard eingebettet.
 * Nutzt Endpoint /api/admin/creator-subscription-bounds (GET/PUT).
 */
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { AlertCircle, Save, Loader2, Euro, TrendingUp, Users } from "lucide-react";

export default function CreatorSubscriptionBoundsPanel() {
  const [config, setConfig] = useState(null);
  const [hardLimits, setHardLimits] = useState({ min_eur: 1.0, max_eur: 999.0 });
  const [minStr, setMinStr] = useState("");
  const [maxStr, setMaxStr] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/creator-subscription-bounds");
      setConfig(data.config);
      setHardLimits(data.hard_limits || { min_eur: 1.0, max_eur: 999.0 });
      setMinStr(String(data.config?.min_price_eur ?? "4.99"));
      setMaxStr(String(data.config?.max_price_eur ?? "49.99"));
    } catch (e) {
      setError(e.response?.data?.detail || "Konnte Konfiguration nicht laden");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError("");
    setSuccess("");
    const mn = Number(String(minStr).replace(",", "."));
    const mx = Number(String(maxStr).replace(",", "."));
    if (!Number.isFinite(mn) || !Number.isFinite(mx)) {
      setError("Bitte gültige Zahlen eingeben (z.B. 4.99)");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/admin/creator-subscription-bounds", {
        min_price_eur: mn,
        max_price_eur: mx,
      });
      setConfig(data.config);
      setSuccess("Preisgrenzen aktualisiert.");
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="p-6 border border-[#E5E5E5] bg-white flex items-center gap-3">
        <Loader2 size={16} className="animate-spin text-[#999]" />
        <span className="text-sm text-[#666]">Lade Preisgrenzen…</span>
      </div>
    );
  }

  return (
    <section data-testid="creator-sub-bounds-panel" className="border border-[#E5E5E5] bg-white p-4 space-y-4">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-[#F5F5F5] border border-[#E5E5E5]">
          <Users size={16} className="text-[#0A0A0A]" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-bold text-brand-black">Creator-Premium-Abo — Preisgrenzen</h3>
          <p className="text-[11px] text-[#666] mt-0.5">
            Globale Mindest- und Höchstpreise für das Premium-Abo eines Creators. Creator legen ihren Preis innerhalb
            dieser Grenzen selbst fest. Änderungen beeinflussen NUR neue oder aktualisierte Preise — bestehende Abos
            bleiben unberührt.
          </p>
        </div>
      </div>

      {error && (
        <div data-testid="creator-sub-bounds-error" className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 text-xs">
          <AlertCircle size={14} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div data-testid="creator-sub-bounds-success" className="p-3 bg-green-50 border border-green-200 text-green-700 text-xs">
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#666] font-semibold">Mindestpreis / Monat</span>
          <div className="relative mt-1">
            <input
              data-testid="creator-sub-bounds-min-input"
              type="number"
              step="0.01"
              min={hardLimits.min_eur}
              max={hardLimits.max_eur}
              value={minStr}
              onChange={(e) => setMinStr(e.target.value)}
              className="w-full h-11 px-4 pr-9 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
            <Euro size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999]" />
          </div>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-[#666] font-semibold">Höchstpreis / Monat</span>
          <div className="relative mt-1">
            <input
              data-testid="creator-sub-bounds-max-input"
              type="number"
              step="0.01"
              min={hardLimits.min_eur}
              max={hardLimits.max_eur}
              value={maxStr}
              onChange={(e) => setMaxStr(e.target.value)}
              className="w-full h-11 px-4 pr-9 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            />
            <Euro size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#999]" />
          </div>
        </label>
      </div>

      <div className="flex items-start gap-2 text-[11px] text-[#666] bg-[#FAFAFA] border border-[#E5E5E5] px-3 py-2">
        <TrendingUp size={12} className="mt-0.5 shrink-0" />
        <span>
          Plattform-Sicherheitsgrenzen: {hardLimits.min_eur.toFixed(2)} € bis {hardLimits.max_eur.toFixed(2)} €.
          Innerhalb dieser Range sind Admin-Werte zulässig.
        </span>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <button
          data-testid="creator-sub-bounds-save"
          onClick={save}
          disabled={saving}
          className="h-11 px-6 bg-[#0A0A0A] text-white text-sm font-medium flex items-center gap-2 hover:bg-[#222] transition-colors disabled:opacity-40"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Speichert…" : "Speichern"}
        </button>
        {config?.updated_at && (
          <p data-testid="creator-sub-bounds-updated-at" className="text-[10px] text-[#999]">
            Zuletzt geändert: {new Date(config.updated_at).toLocaleString("de-DE")}
            {config.updated_by ? ` · Admin: ${config.updated_by}` : ""}
          </p>
        )}
      </div>
    </section>
  );
}
